import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import lottie from 'lottie-web';
import { X, UserRound } from 'lucide-react';
import './TourBot.css';
import { FEATURES, GREETINGS, CHARACTERS, getStepRoute } from './tourContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ===== Helpers ===== */
const authHeaders = () => {
  const t = localStorage.getItem('beatseek_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fetchState = async () => {
  try {
    const { data } = await axios.get(`${API}/tour-bot/state`, { headers: authHeaders() });
    return data;
  } catch { return null; }
};

const saveState = async (patch) => {
  try {
    const { data } = await axios.post(`${API}/tour-bot/state`, patch, { headers: authHeaders() });
    return data;
  } catch { return null; }
};

/* ===== Lottie wrapper ===== */
const LottieAnim = ({ path, className }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const anim = lottie.loadAnimation({
      container: ref.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path,
    });
    return () => anim.destroy();
  }, [path]);
  return <div ref={ref} className={className} />;
};

/* ===== Character avatar (used in widget, picker, dialog mini) ===== */
const CharacterAvatar = ({ character, variant = 'widget' }) => {
  const c = CHARACTERS.find(ch => ch.id === character);
  if (!c) return null;
  if (variant === 'mini') {
    if (c.type === 'image') return <img src={c.asset} alt={c.name} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top' }} />;
    return <LottieAnim path={c.asset} className="tb-mini-lottie" />;
  }
  if (character === 'neon') {
    return (
      <div className="tb-widget-neon">
        <LottieAnim path={c.asset} className="tb-lottie" />
      </div>
    );
  }
  if (character === 'orb') {
    return (
      <div className="tb-widget-orb">
        <LottieAnim path={c.asset} className="tb-lottie" />
      </div>
    );
  }
  // shadow
  return (
    <div className="tb-widget-shadow">
      <img src={c.asset} alt={c.name} />
    </div>
  );
};

/* ===== Picker Modal ===== */
const TourBotPicker = ({ onPick, onSkip }) => {
  const [selected, setSelected] = useState(null);
  return (
    <div className="tb-picker-backdrop" data-testid="tour-bot-picker">
      <div className="tb-picker">
        <h2>Meet Your BEATSEEK Guide</h2>
        <div className="tb-picker-sub">Pick a character to walk you through the platform. You can change your mind anytime.</div>
        <div className="tb-picker-grid">
          {CHARACTERS.map(c => (
            <div
              key={c.id}
              data-character={c.id}
              data-testid={`tour-bot-character-${c.id}`}
              className={`tb-picker-card ${selected === c.id ? 'selected' : ''}`}
              onClick={() => setSelected(c.id)}
            >
              <div className="tb-picker-stage">
                {c.type === 'lottie' ? (
                  <LottieAnim path={c.asset} className="tb-lottie" />
                ) : (
                  <img src={c.asset} alt={c.name} />
                )}
              </div>
              <h3>{c.name}</h3>
              <p>{c.description}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22 }}>
          <button
            className="tb-footer-btn tb-primary"
            data-testid="tour-bot-pick-confirm"
            disabled={!selected}
            style={{ flex: 'none', minWidth: 200, opacity: selected ? 1 : 0.5 }}
            onClick={() => selected && onPick(selected)}
          >
            Choose this guide
          </button>
        </div>
        <button className="tb-picker-skip" data-testid="tour-bot-skip-picker" onClick={onSkip}>
          Maybe later
        </button>
      </div>
    </div>
  );
};

/* ===== Spotlight overlay — highlights one OR multiple DOM elements (binder-tab shape) ===== */
const Spotlight = ({ selector, scrollBlock = 'center', scrollAdjust = 0, offset = null }) => {
  // selector can be a string OR an array of strings (multi-cutout)
  const selectors = useMemo(() => (Array.isArray(selector) ? selector : [selector]).filter(Boolean), [selector]);
  // offset: { x, y, w, h } adjusts the spotlight rect WITHOUT moving the actual element
  const off = offset || {};
  const dx = off.x || 0;
  const dy = off.y || 0;
  const dw = off.w || 0;
  const dh = off.h || 0;
  const [boxes, setBoxes] = useState([]);

  useEffect(() => {
    if (selectors.length === 0) { setBoxes([]); return; }
    // Scroll the FIRST element into view per scrollBlock, then apply optional fine-tune adjustment
    const first = document.querySelector(selectors[0]);
    if (first && first.scrollIntoView) {
      try { first.scrollIntoView({ behavior: 'smooth', block: scrollBlock }); } catch {}
      if (scrollAdjust) {
        setTimeout(() => {
          try { window.scrollBy({ top: scrollAdjust, behavior: 'smooth' }); } catch {}
        }, 400);
      }
    }
    const update = () => {
      const next = selectors
        .map(s => document.querySelector(s))
        .filter(Boolean)
        .map(e => {
          const r = e.getBoundingClientRect();
          return { x: r.left + dx, y: r.top + dy, w: r.width + dw, h: r.height + dh };
        });
      setBoxes(next);
    };
    const initialDelay = setTimeout(update, 350);
    const t = setInterval(update, 400);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(t);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [selectors, scrollBlock, dx, dy, dw, dh]);

  if (boxes.length === 0) return null;
  const padding = 8;
  return (
    <div className="tb-spotlight" data-testid="tour-bot-spotlight">
      {/* SVG mask — dim background everywhere EXCEPT the cutout rect(s) */}
      <svg className="tb-spotlight-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tb-spot-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {boxes.map((b, i) => (
              <rect
                key={i}
                x={b.x - padding}
                y={b.y - padding}
                width={b.w + padding * 2}
                height={b.h + padding * 2}
                rx="12"
                ry="12"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(5, 5, 12, 0.48)" mask="url(#tb-spot-mask)" />
      </svg>
      {/* Yellow rings — one per target */}
      {boxes.map((b, i) => (
        <div
          key={i}
          className="tb-spotlight-ring"
          style={{
            left: b.x - padding,
            top: b.y - padding,
            width: b.w + padding * 2,
            height: b.h + padding * 2,
          }}
        />
      ))}
    </div>
  );
};

/* ===== Dialog Panel — bubble + categories OR step walker ===== */
const TourBotDialog = ({ character, onClose, onChangeCharacter, onMinimize }) => {
  // botMode: 'tour' | 'qa' — top-level pill toggle
  const [botMode, setBotMode] = useState('tour');
  // mode (within tour): 'menu' | 'walk'
  const [mode, setMode] = useState('menu');
  const [activeFeature, setActiveFeature] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1); // 1x, 1.5x, 2x
  const lastAutoRouteRef = useRef(null);

  // ----- Q&A state -----
  const [chatMessages, setChatMessages] = useState([]); // [{role:'user'|'bot', text, suggested_feature_id?, off_topic?}]
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [lastOffTopicIndex, setLastOffTopicIndex] = useState(null);
  const chatThreadRef = useRef(null);

  // Auto-scroll chat thread to latest message
  useEffect(() => {
    if (chatThreadRef.current) {
      chatThreadRef.current.scrollTop = chatThreadRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  // Reset chat when character changes (fresh persona)
  useEffect(() => {
    setChatMessages([]);
    setLastOffTopicIndex(null);
  }, [character]);

  const switchBotMode = (next) => {
    if (next === botMode) return;
    setBotMode(next);
    // If switching to Q&A while in walk, exit walk back to menu
    if (next === 'qa' && mode === 'walk') {
      setMode('menu');
      setActiveFeature(null);
      setStepIdx(0);
      setAutoPlay(false);
      lastAutoRouteRef.current = null;
    }
  };

  const askBot = async (questionText) => {
    const q = (questionText || '').trim();
    if (!q || chatLoading) return;
    // Optimistic user message
    setChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setChatInput('');
    setChatLoading(true);
    try {
      const currentRoute = activeFeature?.route || null;
      const { data } = await axios.post(
        `${API}/tour-bot/ask`,
        {
          question: q,
          character,
          current_route: currentRoute,
          last_off_topic_index: lastOffTopicIndex,
        },
        { headers: authHeaders() }
      );
      if (typeof data?.off_topic_index === 'number') setLastOffTopicIndex(data.off_topic_index);
      setChatMessages(prev => [
        ...prev,
        {
          role: 'bot',
          text: data?.answer || '…',
          suggested_feature_id: data?.suggested_feature_id || null,
          off_topic: !!data?.off_topic,
        },
      ]);
    } catch (e) {
      setChatMessages(prev => [
        ...prev,
        { role: 'bot', text: "Hmm, brain's offline for a sec. Try again?", off_topic: false },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setLastOffTopicIndex(null);
  };

  const visibleFeatures = useMemo(() => {
    // Flat list in nav order (Dashboard first, then ID, Discovery, Lounge, Crate Box, DJ Booth, Producers Corner)
    return FEATURES;
  }, []);

  const startWalkthrough = (featureId) => {
    const feature = FEATURES.find(f => f.id === featureId);
    if (!feature) return;
    setActiveFeature(feature);
    setStepIdx(0);
    setMode('walk');
    setAutoPlay(false);
  };
  const startFullTour = () => {
    setActiveFeature({ id: '__full__', label: 'Full Tour', steps: FEATURES.flatMap(f => f.steps.map(s => ({ ...s, _route: f.route }))) });
    setStepIdx(0);
    setMode('walk');
    setAutoPlay(true);
    lastAutoRouteRef.current = null;
  };
  const exitWalk = () => {
    setMode('menu');
    setActiveFeature(null);
    setStepIdx(0);
    setAutoPlay(false);
    lastAutoRouteRef.current = null;
  };
  const nextStep = () => {
    if (!activeFeature) return;
    if (stepIdx + 1 >= activeFeature.steps.length) exitWalk();
    else setStepIdx(stepIdx + 1);
  };
  const prevStep = () => setStepIdx(Math.max(0, stepIdx - 1));

  // Mobile detection — used to swap in shorter `mobileText` when available
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  // Landscape detection — pill toggle lives in header on landscape, in body on portrait
  const [isLandscape, setIsLandscape] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(orientation: landscape) and (max-width: 1366px) and (max-height: 1024px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape) and (max-width: 1366px) and (max-height: 1024px)');
    const h = (e) => setIsLandscape(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', h);
    else mql.addListener(h);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', h);
      else mql.removeListener(h);
    };
  }, []);

  // "Best on desktop" tip — show once per session on mobile portrait when in tour mode
  const [tourTipDismissed, setTourTipDismissed] = useState(() => {
    try { return localStorage.getItem('tb_tour_tip_dismissed') === '1'; } catch { return false; }
  });
  const dismissTourTip = () => {
    setTourTipDismissed(true);
    try { localStorage.setItem('tb_tour_tip_dismissed', '1'); } catch {}
  };

  // Floating toast: pops up when bot panel opens on mobile portrait (first time only)
  const [showTipToast, setShowTipToast] = useState(false);
  useEffect(() => {
    if (isMobile && !isLandscape && !tourTipDismissed) {
      setShowTipToast(true);
      const t = setTimeout(() => {
        setShowTipToast(false);
        dismissTourTip();
      }, 5500);
      return () => clearTimeout(t);
    }
  }, [isMobile, isLandscape, tourTipDismissed]);

  const currentStep = activeFeature?.steps?.[stepIdx];
  const currentText =
    (isMobile && currentStep?.mobileText?.[character]) ||
    currentStep?.text?.[character] ||
    currentStep?.fallbackText ||
    '';
  const currentRoute = activeFeature?.id === '__full__'
    ? currentStep?._route
    : getStepRoute(activeFeature, currentStep);

  // Per-step page shift: translate the .App container horizontally so right-side highlights are visible
  useEffect(() => {
    const app = document.querySelector('.App');
    if (!app) return;
    const shift = mode === 'walk' ? (currentStep?.pageShiftX || 0) : 0;
    app.style.transition = 'transform 0.35s ease';
    app.style.transform = shift ? `translateX(${shift}px)` : '';
    return () => {
      if (app) {
        app.style.transform = '';
      }
    };
  }, [mode, currentStep]);

  // Auto-navigate to feature route in Full Tour mode whenever the route changes between sections
  const isFullTour = activeFeature?.id === '__full__';
  useEffect(() => {
    if (mode !== 'walk' || !isFullTour) return;
    if (!currentRoute) return;
    if (currentRoute === lastAutoRouteRef.current) return;
    const el = document.querySelector(currentRoute);
    if (el && typeof el.click === 'function') {
      el.click();
    }
    lastAutoRouteRef.current = currentRoute;
  }, [mode, isFullTour, currentRoute, stepIdx]);

  // Auto-advance timer — only active during Full Tour + autoPlay. Dwell scales with text length, divided by playback speed.
  useEffect(() => {
    if (mode !== 'walk' || !isFullTour || !autoPlay) return;
    const text = currentText || '';
    // Reading-aware pacing: ~4s base + 65ms per character, clamped 9s..26s
    const baseDwell = Math.max(9000, Math.min(26000, 4000 + text.length * 65));
    const dwell = Math.round(baseDwell / playSpeed);
    const timer = setTimeout(() => {
      if (stepIdx + 1 >= (activeFeature?.steps?.length || 0)) exitWalk();
      else setStepIdx(stepIdx + 1);
    }, dwell);
    return () => clearTimeout(timer);
  }, [mode, isFullTour, autoPlay, playSpeed, stepIdx, currentText, activeFeature]);

  const goToFeature = useCallback(() => {
    if (!currentRoute) return;
    const el = document.querySelector(currentRoute);
    if (el) {
      el.click();
      // smooth scroll to it after the tab change settles
      setTimeout(() => {
        const t = document.querySelector(currentStep?.target || currentRoute);
        if (t && t.scrollIntoView) t.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 350);
    }
  }, [currentRoute, currentStep]);

  // Pill toggle node — placed in header (landscape) or body (portrait/desktop)
  const pillToggleNode = (
    <div className="tb-mode-pill" role="tablist" aria-label="Bot mode">
      <button
        role="tab"
        aria-selected={botMode === 'tour'}
        className={`tb-mode-pill-btn ${botMode === 'tour' ? 'active' : ''}`}
        onClick={() => switchBotMode('tour')}
        data-testid="tour-bot-mode-tour"
      >
        <span>Tour</span>
      </button>
      <button
        role="tab"
        aria-selected={botMode === 'qa'}
        className={`tb-mode-pill-btn ${botMode === 'qa' ? 'active' : ''}`}
        onClick={() => switchBotMode('qa')}
        data-testid="tour-bot-mode-qa"
      >
        <span className="tb-mode-pill-icon" aria-hidden="true">💬</span>
        <span>Q&amp;A</span>
      </button>
      <span className={`tb-mode-pill-thumb ${botMode === 'qa' ? 'right' : 'left'}`} aria-hidden="true" />
    </div>
  );

  return (
    <>
      {mode === 'walk' && currentStep?.target && <Spotlight selector={currentStep.target} scrollBlock={currentStep?.scrollBlock || 'center'} scrollAdjust={currentStep?.scrollAdjust || 0} offset={currentStep?.offset} />}
      <div className="tb-dialog" data-testid="tour-bot-dialog" role="dialog">
        <div className="tb-dialog-header">
          <div className="tb-dialog-title">
            <span className="tb-mini-avatar">
              {character === 'shadow'
                ? <img src="/bot-preview/shadow_bot_nobg.png" alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top' }} />
                : <span>{character === 'neon' ? '🤖' : '⚪'}</span>}
            </span>
            <span>{CHARACTERS.find(c => c.id === character)?.name || 'Guide'}</span>
          </div>
          {isLandscape && pillToggleNode}
          <div className="tb-dialog-actions">
            <button className="tb-hdr-btn tb-hdr-btn-change" title="Change tour guide character" onClick={onChangeCharacter} data-testid="tour-bot-change-character" aria-label="Change tour guide">
              <UserRound size={14} strokeWidth={2.5} />
            </button>
            <button className="tb-hdr-btn tb-hdr-btn-close" title="Close" onClick={onClose} data-testid="tour-bot-close" aria-label="Close tour">
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="tb-dialog-body">
          {!isLandscape && pillToggleNode}

          {botMode === 'tour' && mode === 'menu' && (
            <>
              <div className="tb-bubble" data-testid="tour-bot-greeting">{GREETINGS[character]}</div>
              <div className="tb-categories">
                <div className="tb-category-label">BEATSEEK Sections</div>
                <div className="tb-features-list">
                  {visibleFeatures.map(f => (
                    <button
                      key={f.id}
                      className="tb-feature-btn"
                      onClick={() => startWalkthrough(f.id)}
                      data-testid={`tour-bot-feature-${f.id}`}
                    >
                      {f.icon && (
                        f.icon === 'home-svg' ? (
                          <span className="tb-emoji" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M3 12 L12 3 L21 12" />
                              <path d="M5 10 V20 H19 V10" />
                              <path d="M10 20 V14 H14 V20" />
                            </svg>
                          </span>
                        ) : f.icon === 'search-svg' ? (
                          <span className="tb-emoji" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <circle cx="10.5" cy="10.5" r="6.5" />
                              <path d="M20 20 L15.5 15.5" />
                            </svg>
                          </span>
                        ) : (
                          <span className="tb-emoji">{f.icon}</span>
                        )
                      )}
                      <span>{f.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {botMode === 'tour' && mode === 'walk' && currentStep && (
            <>
              <div className="tb-bubble" data-testid="tour-bot-step-bubble">{currentText}</div>
              <div className="tb-step-row">
                <div className="tb-step-progress-stack">
                  {activeFeature.id === '__full__' ? (
                    <>
                      <span className="tb-step-feature-name">Full Tour{autoPlay ? ' · Playing' : ' · Paused'}</span>
                      <span className="tb-step-count">{stepIdx + 1} / {activeFeature.steps.length}</span>
                    </>
                  ) : (
                    <>
                      <span className="tb-step-feature-name">{activeFeature.label}</span>
                      <span className="tb-step-count">{stepIdx + 1} / {activeFeature.steps.length}</span>
                    </>
                  )}
                </div>
                {currentRoute && (
                  <button
                    className="tb-go-to-feature"
                    data-testid="tour-bot-go-to-feature"
                    onClick={goToFeature}
                  >
                    Go to Feature →
                  </button>
                )}
              </div>
            </>
          )}
          {botMode === 'qa' && (
            <div className="tb-qa-container">
              {/* Chat thread */}
              <div className="tb-qa-thread" ref={chatThreadRef} data-testid="tour-bot-qa-thread">
                {chatMessages.length === 0 && (
                  <div className="tb-qa-welcome" data-testid="tour-bot-qa-welcome">
                    <div className="tb-bubble">
                      Ask me anything about BEATSEEK — features, where to find things, how stuff works. I'll point you in the right direction.
                    </div>
                    <div className="tb-qa-suggest-label">Try one:</div>
                    <div className="tb-qa-chips">
                      {[
                        'How do I ID a track?',
                        'What is a Jam Session?',
                        'Where are my crates?',
                        'How do I submit a demo?',
                      ].map(chip => (
                        <button
                          key={chip}
                          className="tb-qa-chip"
                          onClick={() => askBot(chip)}
                          data-testid={`tour-bot-qa-chip-${chip.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((m, i) => (
                  <div key={i} className={`tb-qa-msg tb-qa-msg-${m.role}`} data-testid={`tour-bot-qa-msg-${m.role}-${i}`}>
                    <div className="tb-qa-bubble">{m.text}</div>
                    {m.role === 'bot' && m.suggested_feature_id && (
                      <button
                        className="tb-go-to-feature tb-qa-deeplink"
                        onClick={() => {
                          setBotMode('tour');
                          startWalkthrough(m.suggested_feature_id);
                        }}
                        data-testid={`tour-bot-qa-deeplink-${m.suggested_feature_id}`}
                      >
                        Take me there →
                      </button>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="tb-qa-msg tb-qa-msg-bot" data-testid="tour-bot-qa-loading">
                    <div className="tb-qa-bubble tb-qa-typing">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
              </div>

              {/* Input row */}
              <form
                className="tb-qa-input-row"
                onSubmit={(e) => { e.preventDefault(); askBot(chatInput); }}
              >
                <input
                  type="text"
                  className="tb-qa-input"
                  placeholder="Ask me anything about BEATSEEK…"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={chatLoading}
                  data-testid="tour-bot-qa-input"
                  maxLength={400}
                />
                <button
                  type="submit"
                  className="tb-qa-send"
                  disabled={!chatInput.trim() || chatLoading}
                  data-testid="tour-bot-qa-send"
                  aria-label="Send"
                >
                  →
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="tb-dialog-footer">
          {botMode === 'qa' ? (
            <>
              <button
                className="tb-footer-btn"
                data-testid="tour-bot-qa-clear"
                onClick={clearChat}
                disabled={chatMessages.length === 0}
              >
                Clear Chat
              </button>
              <button className="tb-footer-btn" data-testid="tour-bot-close-qa" onClick={onClose}>
                Close
              </button>
            </>
          ) : mode === 'menu' ? (
            <>
              <button className="tb-footer-btn" data-testid="tour-bot-full-tour" onClick={startFullTour}>
                🚀 Full Tour
              </button>
              <button className="tb-footer-btn" data-testid="tour-bot-close-menu" onClick={onClose}>
                Close
              </button>
            </>
          ) : (
            <>
              <button className="tb-footer-btn" data-testid="tour-bot-back-menu" onClick={exitWalk}>
                Back to Start
              </button>
              {isFullTour && (
                <button
                  className="tb-footer-btn tb-autoplay-toggle"
                  data-testid="tour-bot-autoplay-toggle"
                  onClick={() => setAutoPlay(v => !v)}
                  title={autoPlay ? 'Pause auto-play' : 'Resume auto-play'}
                >
                  {autoPlay ? '⏸ Pause' : '▶ Play'}
                </button>
              )}
              {isFullTour && (
                <button
                  className="tb-footer-btn tb-speed-toggle"
                  data-testid="tour-bot-speed-toggle"
                  onClick={() => setPlaySpeed(s => s === 1 ? 2 : s === 2 ? 3 : 1)}
                  title="Cycle playback speed"
                >
                  {playSpeed === 1 ? '1×' : playSpeed === 2 ? '2×' : '3×'}
                </button>
              )}
              {stepIdx > 0 && (
                <button className="tb-footer-btn" data-testid="tour-bot-prev-step" onClick={prevStep}>
                  ← Prev
                </button>
              )}
              <button className="tb-footer-btn tb-skip-next" data-testid="tour-bot-next-step" onClick={nextStep}>
                {stepIdx + 1 >= activeFeature.steps.length ? 'Done' : 'Skip / Next →'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Floating "best on desktop" toast — mobile portrait only, first time only */}
      {showTipToast && (
        <div className="tb-tip-toast" data-testid="tour-bot-mobile-tip-toast" role="status" aria-live="polite">
          <span className="tb-tip-toast-icon" aria-hidden="true">💡</span>
          <span className="tb-tip-toast-text">
            The Virtual Tour is best experienced on desktop, laptop, or tablet.
          </span>
          <button
            className="tb-tip-toast-close"
            onClick={() => { setShowTipToast(false); dismissTourTip(); }}
            aria-label="Dismiss tip"
            data-testid="tour-bot-mobile-tip-close"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

/* ===== Main TourBot — orchestrates state, widget, picker, dialog ===== */
const getToken = () =>
  (typeof window !== 'undefined' &&
    (localStorage.getItem('beatseek_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('token'))) || null;

// True only when the user is actually logged into the app shell (not merely a lingering token)
const getStoredUser = () =>
  (typeof window !== 'undefined' && localStorage.getItem('beatseek_user')) || null;

const TourBot = () => {
  const [state, setState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [token, setToken] = useState(getToken());
  const [hasUser, setHasUser] = useState(!!getStoredUser());
  const [activeTab, setActiveTab] = useState(window.__beatseekActiveTab || 'social');
  const [pathname, setPathname] = useState(window.location.pathname);

  // Poll for token changes (login / logout)
  useEffect(() => {
    const tick = () => {
      const t = getToken();
      setToken(prev => (prev !== t ? t : prev));
      const h = !!getStoredUser();
      setHasUser(prev => (prev !== h ? h : prev));
      setPathname(prev => (prev !== window.location.pathname ? window.location.pathname : prev));
    };
    const interval = setInterval(tick, 1500);
    window.addEventListener('storage', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', tick);
      window.removeEventListener('focus', tick);
    };
  }, []);

  // Track active dashboard tab (broadcast from App)
  useEffect(() => {
    const onTab = (e) => setActiveTab(e.detail);
    window.addEventListener('beatseek-active-tab', onTab);
    return () => window.removeEventListener('beatseek-active-tab', onTab);
  }, []);

  // Fetch state whenever token becomes available
  useEffect(() => {
    if (!token) { setLoaded(true); setState(null); return; }
    let cancelled = false;
    fetchState().then(s => {
      if (cancelled) return;
      setState(s);
      setLoaded(true);
      if (s && !s.has_seen_picker && !s.character) {
        setShowPicker(true);
      }
    });
    return () => { cancelled = true; };
  }, [token]);

  const pickCharacter = useCallback(async (char) => {
    const updated = await saveState({ character: char, has_seen_picker: true, bump_opened: true });
    setState(updated);
    setShowPicker(false);
    setShowDialog(true);
  }, []);

  const skipPicker = useCallback(async () => {
    await saveState({ has_seen_picker: true });
    setState(prev => ({ ...(prev || {}), has_seen_picker: true }));
    setShowPicker(false);
  }, []);

  const openDialog = useCallback(async () => {
    if (!state?.character) {
      setShowPicker(true);
      return;
    }
    setShowDialog(true);
    await saveState({ bump_opened: true });
  }, [state]);

  // Open tour from footer link (mobile tabs where the widget is hidden)
  useEffect(() => {
    const open = () => openDialog();
    window.addEventListener('beatseek-open-tour', open);
    return () => window.removeEventListener('beatseek-open-tour', open);
  }, [openDialog]);

  const changeCharacter = useCallback(() => {
    setShowDialog(false);
    setShowPicker(true);
  }, []);

  // Only ever show once the user is actually logged into the app shell (beatseek_user present) —
  // never on the login/sign-up screen, even if a stale token lingers in localStorage.
  if (!hasUser || !loaded || !token) return null;

  // Floating avatar widget lives ONLY on the main Dashboard (all screen sizes).
  // Every other main tab uses the "✨ Take the tour" text link instead; inside
  // features neither should appear.
  const hideWidget = !(pathname === '/' && activeTab === 'social');

  const character = state?.character || 'neon';
  const showFirstTimeBadge = !state?.character;

  return (
    <>
      {showPicker && (
        <TourBotPicker onPick={pickCharacter} onSkip={skipPicker} />
      )}
      {showDialog && state?.character && (
        <TourBotDialog
          character={character}
          onClose={() => setShowDialog(false)}
          onChangeCharacter={changeCharacter}
          onMinimize={() => setShowDialog(false)}
        />
      )}
      {!showPicker && !hideWidget && (
        <button
          className="tb-widget"
          data-testid="tour-bot-widget"
          aria-label="Open BEATSEEK Tour Guide"
          onClick={openDialog}
        >
          <CharacterAvatar character={character} />
          {showFirstTimeBadge && <span className="tb-widget-badge" />}
          <span className="tb-widget-label">Virtual Tour? Q&amp;A?</span>
        </button>
      )}
    </>
  );
};

export default TourBot;
